/**
 * EventApplyPage — the public event page on the Portal vocabulary
 * (docs/plans/2026-09-event-page/handoff README: §1 variants, §2 desktop + mobile).
 *
 * Desktop: `← EVENTS / NAME` → the cover (EventHeader: photos in the host's layout, text column
 * on top) → a grid of the content column (status band · pill tabs · ABOUT / GUESTS / Q + A /
 * GIVE / TAKE / WHERE) and the sticky 380px checkout rail (static under the content below 1040px).
 * Mobile (< 1024, and always when `embedded`): the collapsing title bar → the photo cover →
 * identity card → rounded band → 3-up stats → scrolling pill tabs → tab content → the black
 * sticky price + CTA pill opening the checkout bottom sheet.
 *
 * The whole page renders inside `SoftSurface`, so every surface-kit piece draws borderless
 * panels, pills and the ring. Copy and variant logic are unchanged: `eventPageModel` /
 * `checkoutRailModel` decide, this file lays out.
 *
 * Props: `inviteCode` (from MobileEventSheet — hides the route param), `embedded` (no chrome,
 * inside the vaul drawer), `previewEvent` (an unpublished EventDto: no network, inert page),
 * `previewFrame` ('desktop' | 'mobile' — the review step's previews force a frame regardless
 * of the viewport) and `coverEdit` ({ photos, layout, split, onFocal }: the cover draws these
 * photos and lets the host drag each one to reframe it).
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { webOrigin } from '../utils/brand';
import { peek } from '../data/routeData';
import { eventKey, loadEvent } from '../data/routeLoaders';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { ErrorState } from '../components/EmptyState';
import ShareSheet from '../components/ShareSheet';
import { eventCardUrl } from '../utils/shareCard';
import MediaViewer from '../components/MediaViewer';
import { eventService, applicationService, friendService } from '../services';
import mimeService from '../services/mimeService';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import useIsMobile from '../hooks/useIsMobile';
import useMediaQuery from '../utils/useMediaQuery';
import useImageRatios from '../hooks/useImageRatios';
import { getPreviousPath } from '../utils/backNav';
import { crumb } from '../utils/surfaceFormat';
import {
  SurfacePage, Breadcrumb, StatusBand, SegmentedTabs, MobileTabStrip, StickyRail, BottomSheet, StickyCtaBar, StatStrip, SoftSurface, SOFT,
} from '../components/surface';
import { railModel, GOING_STATUSES } from '../components/event/checkoutRailModel';
import EventCheckoutRail, { RailShareRow } from '../components/event/EventCheckoutRail';
import EventGuestListModal from '../components/event/EventGuestListModal';
import { describeEvent, mediaItems, eventTabs } from '../components/event/eventPageModel';
import { allCoverPhotos, coverPhotos } from '../components/event/coverLayout';
import EventHeader, { EventIdentityMobile, EventCoverMobile } from '../components/event/EventHeader';
import EventAboutTab from '../components/event/EventAboutTab';
import EventGuestsTab from '../components/event/EventGuestsTab';
import EventQaTab from '../components/event/EventQaTab';
import EventBoardTab from '../components/event/EventBoardTab';
import EventWhereTab from '../components/event/EventWhereTab';
import EventSuccessScreen from '../components/event/EventSuccessScreen';

const GUEST_LIST_STATUSES = [...GOING_STATUSES, 'external_interest'];
/** The rail sits beside the content from 1040px (handoff §2); below that it goes under it. */
const WIDE_QUERY = '(min-width: 1040px)';
const DESKTOP_FRAME = 1280;
const DESKTOP_PAD = 32;
const MOBILE_FRAME = 390;

/** The mobile CTA pill: magenta for a Party Starter, cyan off-platform or with a ticket in hand. */
export function mobileCtaBg(variant) {
  if (variant === 'starter') return SOFT.magenta;
  if (variant === 'external' || variant === 'held') return SOFT.cyan;
  return '#fff';
}

// Stable empty list, so a viewer with no MIME round does not rebuild the tab list each render.
const NO_MIME = [];

export default function EventApplyPage({ inviteCode: inviteCodeProp = null, embedded = false, previewEvent = null, previewFrame = null, coverEdit = null } = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // When rendered inside the mobile bottom-sheet we get the code as a prop and hide the
  // page chrome; on its own route it comes from the URL. It is an invite code first, and
  // the fetch falls back to a lookup by id.
  // Three ways in, one identifier: the mobile sheet passes a code as a prop, /events/:inviteCode
  // and /e/:inviteCode put it in the path, and the canonical /e/:citySlug/:slug arrives as two
  // params that we join with the slash the loader keys off.
  const eventId = inviteCodeProp
    || (params.citySlug && params.slug ? `${params.citySlug}/${params.slug}` : null)
    || params.inviteCode;
  const { isAuthenticated } = useAuth();
  const { profile, walletBalance, refreshWallet } = useUser();
  const viewportMobile = useIsMobile();
  const viewportWide = useMediaQuery(WIDE_QUERY);
  // The drawer is 630px on desktop — the desktop layout (tabs + 380px rail) cannot fit, so
  // embedded always renders the mobile frame. A preview frame overrides both.
  const mobile = previewFrame ? previewFrame === 'mobile' : (viewportMobile || embedded);
  const wide = previewFrame ? previewFrame === 'desktop' : viewportWide;
  const preview = !!previewEvent;
  const framed = !!previewFrame;
  const coverWidth = framed ? (mobile ? MOBILE_FRAME : DESKTOP_FRAME - DESKTOP_PAD * 2) : undefined;

  const cached = peek(eventKey(eventId));
  const authSeen = useRef(null);
  const [event, setEvent] = useState(previewEvent || cached?.event || null);
  const [isLoading, setIsLoading] = useState(!previewEvent && !cached);
  const [error, setError] = useState(null);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [submittedApplication, setSubmittedApplication] = useState(null);
  const [existingApplication, setExistingApplication] = useState(cached?.application || null);
  const [mimeRequests, setMimeRequests] = useState(() => (cached?.mime || []).filter(r => r.status === 'Pending'));
  const [mimeHistory, setMimeHistory] = useState(() => (cached?.mime || []).filter(r => r.status !== 'Pending'));
  const [respondingMimeId, setRespondingMimeId] = useState(null);
  const [mimeErrors, setMimeErrors] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(!!cached?.event?.isBookmarked);
  const [goldenTicketsEnabled, setGoldenTicketsEnabled] = useState(cached?.golden !== false);
  const [guestListOpen, setGuestListOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [guestList, setGuestList] = useState(null);
  const [guestLoaded, setGuestLoaded] = useState(false);
  const [guestDenied, setGuestDenied] = useState(false);
  useEffect(() => { setGuestDenied(false); }, [eventId]);
  const [guestSaving, setGuestSaving] = useState(false);
  const [friendIds, setFriendIds] = useState(() => new Set());
  // Confirmed friends only (userId → isClose), for the guest list's close-friends
  // mark. friendIds also picks up people you just sent a request to.
  const [friendCloseMap, setFriendCloseMap] = useState(null);
  const [localTab, setLocalTab] = useState('about');

  // ── tabs: client state, `?tab=` deep link on the real route (ProPage pattern) ──────
  // The tab list itself is computed further down (it depends on the event and the viewer's
  // MIME round), and `tab` with it — a `?tab=` key this event has no tab for falls to ABOUT.
  const urlTab = searchParams.get('tab');
  const setTab = useCallback((key) => {
    if (embedded || preview) { setLocalTab(key); return; }
    const next = new URLSearchParams(searchParams);
    if (key === 'about') next.delete('tab'); else next.set('tab', key);
    setSearchParams(next, { replace: true });
  }, [embedded, preview, searchParams, setSearchParams]);

  // ── load: flags → event by code (falls back to id) → viewer's application → MIME ──
  useEffect(() => {
    if (previewEvent) {
      // Preview: nothing to fetch, nothing to check — the DTO is the whole truth.
      setEvent(previewEvent);
      setIsLoading(false);
      return;
    }
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(!peek(eventKey(eventId)));
      setError(null);
      try {
        // Whether to ask for the viewer's application is decided by the stored token (the
        // loader's default), not by `isAuthenticated`: on a hard refresh that flag is false until
        // the session is restored, and a load made with it cached `application: null` for 30s —
        // the rail then showed GET TICKETS over an abandoned pending_payment row and the second
        // buy was refused as a duplicate. When the flag does flip while the page is open (sign
        // in / out from here), the cached read is stale, so that re-run forces a fresh one.
        const flipped = authSeen.current != null && authSeen.current !== isAuthenticated;
        authSeen.current = isAuthenticated;
        const data = await loadEvent(eventId, { authenticated: isAuthenticated, force: flipped });
        if (cancelled) return;
        setEvent(data.event);
        setIsBookmarked(!!data.event.isBookmarked);
        setGoldenTicketsEnabled(data.golden);
        setExistingApplication(data.application);
        setMimeHistory(data.mime.filter(r => r.status !== 'Pending'));
        setMimeRequests(data.mime.filter(r => r.status === 'Pending'));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, isAuthenticated, previewEvent]);

  // ── guest list (server rule: host / any co-host / staff — `viewerCanViewGuests` — or an
  // application in accepted · paid · external_interest). `viewerCanEdit` is kept as a signal for
  // bundles answered by an older API. A refusal from the server (a stale flag) falls back to
  // the GUESTS ONLY copy rather than an empty list that reads as "nobody bought".
  const canViewGuests = isAuthenticated && !preview && !!event
    && (!!event.viewerCanViewGuests || !!event.viewerCanEdit || GUEST_LIST_STATUSES.includes(existingApplication?.status))
    && !guestDenied;
  useEffect(() => {
    if (!canViewGuests || !event?.id) { setGuestLoaded(false); setGuestList(null); return undefined; }
    let cancelled = false;
    (async () => {
      const res = await eventService.getGuestList(event.id);
      if (!cancelled) {
        if (res?.success) setGuestList(res.data); else setGuestDenied(true);
        setGuestLoaded(true);
      }
      const fr = await friendService.getFriends();
      if (!cancelled && fr?.success) {
        setFriendIds(new Set((fr.data || []).map((f) => f.userId)));
        setFriendCloseMap(new Map((fr.data || []).map((f) => [f.userId, !!f.isClose])));
      }
    })();
    return () => { cancelled = true; };
  }, [canViewGuests, event?.id]);

  const toggleGuestVisibility = async (show) => {
    setGuestSaving(true);
    try {
      const res = await eventService.setGuestListVisibility(event.id, show);
      if (res?.success) {
        const refreshed = await eventService.getGuestList(event.id);
        if (refreshed?.success) setGuestList(refreshed.data);
      }
    } finally {
      setGuestSaving(false);
    }
  };

  // ── `?rate=1`: arriving from the RATE IT banner — open the rail's rate panel + scroll ──
  useEffect(() => {
    if (!event || preview) return undefined;
    if (new URLSearchParams(window.location.search).get('rate') !== '1') return undefined;
    if (mobile) setSheetOpen(true);
    const t1 = window.setTimeout(() => {
      if (!document.getElementById('post-event-feedback')) {
        document.querySelector('[data-testid="checkout-rail"][data-variant="ended"] [data-testid="rail-cta"]')?.click();
      }
      window.setTimeout(() => document.getElementById('post-event-feedback')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }, 400);
    return () => window.clearTimeout(t1);
  }, [event, mobile, preview]);

  // ── handlers ───────────────────────────────────────────────────────────────────────
  // The mobile price + CTA bar. Off-platform events go straight to the seller (one tap, and
  // the interest ping the desktop rail sends); a host taps through to the dashboard; every
  // other variant opens the checkout sheet.
  const handleMobileCta = async () => {
    if (!rail) return;
    if (rail.variant === 'host') { navigate(`/host/events/${event.id}`); return; }
    if (rail.variant === 'external') {
      if (event.externalTicketUrl) window.open(event.externalTicketUrl, '_blank', 'noopener,noreferrer');
      if (isAuthenticated && existingApplication?.status !== 'external_interest' && !preview) {
        const res = await eventService.registerExternalInterest(event.id);
        if (res?.success) {
          const appRes = await applicationService.getMyApplicationForEvent(event.id);
          if (appRes?.success) setExistingApplication(appRes.data || null);
        }
      }
      return;
    }
    setSheetOpen(true);
  };

  const handleSuccess = (application) => {
    setSubmittedApplication(application);
    if (application) setExistingApplication(application);
    setApplicationSuccess(true);
    setSheetOpen(false);
  };

  const handleMimeAccept = async (requestId) => {
    setRespondingMimeId(requestId);
    setMimeErrors((prev) => ({ ...prev, [requestId]: null }));
    const result = await mimeService.respondToRequest(requestId, true);
    if (result?.success) {
      setMimeRequests((prev) => {
        const accepted = prev.find((r) => r.id === requestId);
        if (accepted) setMimeHistory((h) => [...h, { ...accepted, status: 'Accepted', committedAmountCents: accepted.additionalCommitmentCents }]);
        return prev.filter((r) => r.id !== requestId);
      });
      refreshWallet?.();
    } else {
      setMimeErrors((prev) => ({ ...prev, [requestId]: result?.error?.message || result?.error || 'Failed to commit' }));
    }
    setRespondingMimeId(null);
  };

  const handleMimeDecline = async (requestId) => {
    setRespondingMimeId(requestId);
    setMimeErrors((prev) => ({ ...prev, [requestId]: null }));
    const result = await mimeService.respondToRequest(requestId, false);
    if (result?.success) {
      setMimeRequests((prev) => {
        const declined = prev.find((r) => r.id === requestId);
        if (declined) setMimeHistory((h) => [...h, { ...declined, status: 'Declined' }]);
        return prev.filter((r) => r.id !== requestId);
      });
    } else {
      setMimeErrors((prev) => ({ ...prev, [requestId]: result?.error?.message || result?.error || 'Failed to decline' }));
    }
    setRespondingMimeId(null);
  };

  const toggleBookmark = async () => {
    if (!event?.id || preview) return;
    const result = await eventService.toggleBookmark(event.id);
    if (result?.success) setIsBookmarked(!!result.data?.isBookmarked);
  };

  // BACK is a real history pop whenever we arrived from somewhere inside the app (a gathering,
  // a club, a Portal Page, the wall — scroll comes back too); only a cold open of the event
  // page falls back to the events wall (Oct 20 2026 — it used to send everyone to /featured).
  const cameFromInside = !!getPreviousPath();
  const backLabel = cameFromInside ? 'BACK' : 'EVENTS';
  const goBackToEvents = () => {
    if (cameFromInside) navigate(-1);
    else navigate('/featured');
  };

  const signInPath = `/login?redirect=/events/${eventId || event?.inviteCode || ''}`;
  const goSignIn = () => navigate(signInPath);

  // Open a Portal for this event — tells your people you're going. Pre-fills the composer
  // with the event's identity, time and location (the id is what links it back).
  const goPortal = () => navigate('/portals/new', { state: { fromEvent: {
    eventId: event?.id ?? null,
    title: event?.title,
    startUtc: event?.dateTime,
    endUtc: event?.endDateTime || null,
    lat: event?.locationLatitude ?? null,
    lng: event?.locationLongitude ?? null,
    locationLabel: event?.venueName || event?.locationCity || event?.locationAddress || null,
    defaultText: `Going to ${event?.title}`,
  } } });

  // ── derived ────────────────────────────────────────────────────────────────────────
  const viewer = useMemo(() => ({ isAuthenticated, profile, walletBalance, refreshWallet }), [isAuthenticated, profile, walletBalance, refreshWallet]);
  const friendIdList = useMemo(() => Array.from(friendIds), [friendIds]);
  const friendsIn = useMemo(() => {
    if (!canViewGuests || !guestList) return null;
    return (guestList.entries || []).filter((g) => g.userId && !g.isViewer && friendIds.has(g.userId)).length;
  }, [canViewGuests, guestList, friendIds]);
  const guestCount = canViewGuests && guestList ? (guestList.shownCount || 0) + (guestList.anonymousCount || 0) : null;
  const model = useMemo(() => (event ? describeEvent({ event, application: existingApplication, viewer, friendsIn, guestCount }) : null), [event, existingApplication, viewer, friendsIn, guestCount]);
  const rail = useMemo(() => (event ? railModel({ event, application: existingApplication, viewer, goldenTicketsEnabled }) : null), [event, existingApplication, viewer, goldenTicketsEnabled]);

  // Photos: the poster + gallery images in the host's cover order, shaped by their measured
  // ratios (the side / poster layouts follow the photo). The review step hands its own list in.
  const dtoSrcs = useMemo(() => allCoverPhotos(event).map((p) => p.src), [event]);
  const ratios = useImageRatios(dtoSrcs);
  const allPhotos = useMemo(() => allCoverPhotos(event, ratios), [event, ratios]);
  const dtoPhotos = useMemo(() => coverPhotos(event, ratios), [event, ratios]);
  const photos = coverEdit?.photos || dtoPhotos;
  const layout = coverEdit?.layout || event?.coverLayout || 'auto';
  const split = coverEdit?.split || event?.coverSplit || 60;
  // The media viewer: every photo in cover order, then the videos.
  const items = useMemo(() => {
    const videos = mediaItems(event).filter((m) => m.kind && m.kind !== 'image');
    return [...allPhotos.map((p) => ({ id: p.id, kind: 'image', url: p.src, thumbnailUrl: p.src })), ...videos];
  }, [event, allPhotos]);
  const openViewer = (i) => { if (!preview && items.length > 0) setViewerIndex(Math.max(0, Math.min(i || 0, items.length - 1))); };
  const isHostViewer = !!event && (!!event.viewerCanEdit || (!!profile?.id && profile.id === event.hostId));
  const declined = existingApplication?.status === 'declined';
  // What we hand out when someone shares. Prefer the canonical /e/{city}/{slug} — it reads as a
  // real address and is what search should index. The invite-code URL remains a working way in,
  // it just stops being the one we advertise. Falls back to it when an event has no slug (no
  // usable city was derivable).
  const origin = webOrigin();
  const canonicalPath = event?.citySlug && event?.slug
    ? `/e/${event.citySlug}/${event.slug}`
    : `/events/${event?.inviteCode || eventId || ''}`;
  const pageUrl = `${origin}${canonicalPath}`;

  // Every card in the app still links by invite code, and rewriting ~30 call sites (across DTOs
  // that don't even carry the slug yet) would be a much wider change than this. Once the event is
  // loaded we know its canonical address, so swap the bar over to it with a REPLACE — no history
  // entry, so Back still goes where the guest came from, and the invite-code URL keeps working
  // for anyone who follows one. Skipped for the embedded sheet and previews, which aren't routes.
  // A hidden gathering pass event has no page of its own: its public address is the gathering.
  useEffect(() => {
    if (preview || embedded) return;
    if (event?.isGatheringPass && event?.gatheringId) navigate(`/gatherings/${event.gatheringId}`, { replace: true });
  }, [event?.isGatheringPass, event?.gatheringId, preview, embedded, navigate]);

  useEffect(() => {
    if (preview || embedded) return;
    if (event?.isGatheringPass) return;
    if (!event?.citySlug || !event?.slug) return;
    const canonical = `/e/${event.citySlug}/${event.slug}`;
    if (typeof window === 'undefined') return;
    if (window.location.pathname === canonical) return;
    navigate(`${canonical}${window.location.search}${window.location.hash}`, { replace: true });
  }, [event?.citySlug, event?.slug, event?.isGatheringPass, preview, embedded, navigate]);

  // Both addresses serve the same page on purpose, so tell crawlers which one counts.
  useEffect(() => {
    if (preview || embedded || typeof document === 'undefined') return;
    if (!event?.citySlug || !event?.slug) return;
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement('link');
      tag.setAttribute('rel', 'canonical');
      document.head.appendChild(tag);
    }
    tag.setAttribute('href', `${origin}/e/${event.citySlug}/${event.slug}`);
    return () => { tag?.parentNode?.removeChild(tag); };
  }, [event?.citySlug, event?.slug, origin, preview, embedded]);

  // The viewer's MIME cards, which only the board tab draws — the same values the tab gets,
  // so the tab is never there for an empty round.
  const shownMime = useMemo(() => (isAuthenticated && existingApplication && !declined ? mimeRequests : NO_MIME), [isAuthenticated, existingApplication, declined, mimeRequests]);
  const shownMimeHistory = useMemo(() => (isAuthenticated && existingApplication ? mimeHistory : NO_MIME), [isAuthenticated, existingApplication, mimeHistory]);
  const tabs = useMemo(() => eventTabs({ event, mimeRequests: shownMime, mimeHistory: shownMimeHistory }).map((t) => ({
    ...t,
    gated: !isAuthenticated && ['guests', 'qa', 'board'].includes(t.key),
    count: t.key === 'board' && shownMime.length > 0 ? shownMime.length : undefined,
  })), [event, isAuthenticated, shownMime, shownMimeHistory]);
  const wantedTab = (embedded || preview) ? localTab : urlTab;
  const tab = tabs.some((t) => t.key === wantedTab) ? wantedTab : 'about';

  // ── frames ─────────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SoftSurface>
        <SurfacePage embedded={embedded} mobileTitle="LOADING…" onBack={goBackToEvents} mobile={framed ? mobile : null}>
          <div className="flex items-center justify-center" style={{ minHeight: 320 }}><LoadingSpinner size="lg" label="Loading event..." /></div>
        </SurfacePage>
      </SoftSurface>
    );
  }
  if (error || !event || !model || !rail) {
    return (
      <SoftSurface>
        <SurfacePage embedded={embedded} mobileTitle="EVENT" onBack={goBackToEvents} mobile={framed ? mobile : null}>
          <div className="flex items-center justify-center" style={{ minHeight: 320 }}><ErrorState message={error || 'Event not found'} onRetry={() => window.location.reload()} /></div>
        </SurfacePage>
      </SoftSurface>
    );
  }

  if (applicationSuccess) {
    return (
      <SoftSurface>
        <SurfacePage embedded={embedded} mobileTitle={event.title} mobileRight="✓" onBack={goBackToEvents}>
          <EventSuccessScreen event={event} application={submittedApplication} mobile={mobile} onDashboard={() => navigate('/guest')} onBrowse={() => navigate('/guest')} />
        </SurfacePage>
      </SoftSurface>
    );
  }

  const railNode = (mode) => (
    <EventCheckoutRail
      event={event}
      application={existingApplication}
      viewer={viewer}
      mode={mode}
      preview={preview}
      onApplicationChange={(app) => setExistingApplication(app)}
      onSuccess={handleSuccess}
      onOpenGuestList={() => { setTab('guests'); setGuestListOpen(true); }}
      onShare={() => setShareOpen(true)}
      onSave={toggleBookmark}
      isBookmarked={isBookmarked}
      onPortal={isAuthenticated && !declined ? goPortal : undefined}
      goldenTicketsEnabled={goldenTicketsEnabled}
    />
  );

  const tabBody = (
    <>
      {tab === 'about' && <EventAboutTab event={event} model={model} mobile={mobile} items={items} onOpenPhoto={openViewer} />}
      {tab === 'guests' && (
        <EventGuestsTab
          isAuthenticated={isAuthenticated}
          canView={canViewGuests}
          guestList={guestList}
          loaded={guestLoaded}
          friendIds={friendIds}
          onOpenAll={() => setGuestListOpen(true)}
          onToggleVisibility={toggleGuestVisibility}
          saving={guestSaving}
          mobile={mobile}
          onSignIn={goSignIn}
        />
      )}
      {tab === 'qa' && <EventQaTab eventId={event.id} isHost={isHostViewer} isAuthenticated={isAuthenticated} mobile={mobile} onSignIn={goSignIn} />}
      {tab === 'board' && (
        <EventBoardTab
          eventId={event.id}
          giveTakeEnabled={event.giveTakeEnabled}
          isAuthenticated={isAuthenticated}
          mobile={mobile}
          onSignIn={goSignIn}
          mimeRequests={shownMime}
          mimeHistory={shownMimeHistory}
          application={existingApplication}
          onMimeAccept={handleMimeAccept}
          onMimeDecline={handleMimeDecline}
          respondingMimeId={respondingMimeId}
          mimeErrors={mimeErrors}
        />
      )}
      {tab === 'where' && <EventWhereTab event={event} note={model.whereNote} mobile={mobile} pageUrl={pageUrl} />}
    </>
  );

  const overlays = (
    <>
      <ShareSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={event.title || ''}
        venue={event.venueName}
        city={event.locationCity}
        url={pageUrl}
        inviteCode={event.inviteCode || eventId}
        date={event.dateTime}
        status={event.status}
        mode={event.mode}
        posterUrl={event.posterUrl}
        cardUrl={eventCardUrl(event.inviteCode)}
      />
      <EventGuestListModal
        open={guestListOpen}
        onClose={() => setGuestListOpen(false)}
        event={event}
        canView={canViewGuests}
        mobile={mobile}
        viewerFriendIds={guestLoaded ? friendIdList : null}
        viewerCloseMap={guestLoaded ? friendCloseMap : null}
        onAddFriend={(userId) => setFriendIds((s) => new Set(s).add(userId))}
      />
      {viewerIndex !== null && items.length > 0 && (
        <MediaViewer items={items} index={viewerIndex} onIndex={setViewerIndex} onClose={() => setViewerIndex(null)} title={event.title} />
      )}
    </>
  );

  // A preview is inert. With a cover editor the cover keeps its pointer events (the drags
  // have to land) and everything else is switched off piece by piece.
  const editing = !!coverEdit;
  const inertAll = preview && !editing ? 'pointer-events-none select-none' : '';
  const inertRest = preview && editing ? 'pointer-events-none select-none' : '';
  const coverProps = { photos, layout, split, onOpenPhoto: openViewer, editable: editing, onFocal: coverEdit?.onFocal, width: coverWidth };

  // ── mobile / embedded ──────────────────────────────────────────────────────────────
  if (mobile) {
    const bandNode = model.band ? (
      <div className={inertRest} style={{ margin: '0 12px' }}>
        <StatusBand label={model.band.label} value={model.band.value} note={model.band.note} bg={model.band.bg} fg={model.band.fg} sticky={false} mobile testId={`status-band-${model.band.kind}`} />
      </div>
    ) : null;
    // Cover → identity → band → stats → tab strip → tab content. On the route this sits in the
    // window's scroll (title bar and CTA bar sticky); embedded it sits in its own scroller.
    const body = (
      <div className="flex flex-col" style={{ gap: 10 }}>
        <EventCoverMobile {...coverProps} />
        <div className={inertRest}><EventIdentityMobile event={event} model={model} /></div>
        {/* SHARE · SAVE · PORTAL on the page itself (Oct 23 2026). Until now a phone could only share
            from inside the checkout sheet, behind GET TICKETS — and an off-platform event's GET
            TICKETS goes straight to the seller, so that sheet never opened and the event could not
            be shared at all. White pills on the canvas, like the desktop rail. */}
        {!preview && (
          <div className={inertRest} style={{ margin: '0 12px' }}>
            <RailShareRow onShare={() => setShareOpen(true)} onSave={toggleBookmark} isBookmarked={isBookmarked} onPortal={isAuthenticated && !declined ? goPortal : undefined} testId="page-share-row" />
          </div>
        )}
        {bandNode}
        <div className={inertRest}><StatStrip stats={model.mobStats} mobile testId="mobile-stats" /></div>
        <div className={inertRest}><MobileTabStrip tabs={tabs} active={tab} onChange={setTab} stickyTop={embedded || framed ? 0 : undefined} /></div>
        <div className={`flex flex-col ${inertRest}`} style={{ padding: '0 12px', gap: 10, minHeight: framed ? 0 : 680 }} data-testid="mobile-tab-content">
          {tabBody}
        </div>
      </div>
    );
    const ctaBar = <StickyCtaBar price={rail.price} note={rail.mobNote} cta={rail.mobCta} onCta={handleMobileCta} disabled={preview} aboveTabBar={isAuthenticated && !embedded} sticky={!embedded || framed} ctaBg={mobileCtaBg(rail.variant)} />;
    return (
      <SoftSurface>
        <div className={`${inertAll} ${embedded ? 'flex min-h-0 flex-1 flex-col' : ''}`} aria-disabled={preview ? 'true' : undefined} data-testid="event-page-mobile" style={{ background: SOFT.canvas }}>
          <SurfacePage embedded={embedded} mobileTitle={event.title} mobileRight={rail.price} onBack={goBackToEvents} mobile={framed ? true : null} className={embedded ? 'flex min-h-0 flex-1 flex-col' : ''}>
            {embedded && !framed && (
              // The drawer has no title bar. This strip is a flex child ABOVE the page's own
              // scroller (not sticky inside it), so nothing can ever peek over it; the tab strip
              // therefore sticks at 0 in that scroller.
              <div data-mob-header="1" className="flex flex-none items-center gap-[10px] bg-white" style={{ padding: '12px 16px' }} data-testid="embedded-title-bar">
                <span className="min-w-0 flex-1 truncate font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.05em' }}>{event.title}</span>
                <span className="flex-none font-mono font-bold" style={{ fontSize: 12 }}>{rail.price}</span>
              </div>
            )}
            {embedded && !framed ? (
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="embedded-scroll">
                {body}
              </div>
            ) : body}
            {inertRest ? <div className={inertRest}>{ctaBar}</div> : ctaBar}
          </SurfacePage>
          <BottomSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={rail.band}
            note={rail.bandRight}
            zIndex={embedded ? 70 : 60}
            // SHARE sits in the sheet's header so it is on screen the moment the sheet opens; the
            // SHARE · SAVE · PORTAL row under the checkout body mirrors the desktop rail.
            action={!preview && (
              <button type="button" onClick={() => setShareOpen(true)} className="flex-none rounded-full font-mono font-bold uppercase transition-colors duration-200 hover:bg-black hover:text-white" style={{ height: 38, padding: '0 16px', fontSize: 10, letterSpacing: '0.14em', background: '#F2F2F2' }} data-testid="sheet-share">SHARE</button>
            )}
          >
            {railNode('sheet')}
          </BottomSheet>
          {overlays}
        </div>
      </SoftSurface>
    );
  }

  // ── desktop ────────────────────────────────────────────────────────────────────────
  const bandNode = model.band ? <StatusBand label={model.band.label} value={model.band.value} note={model.band.note} bg={model.band.bg} fg={model.band.fg} sticky={false} testId={`status-band-${model.band.kind}`} /> : null;
  return (
    <SoftSurface>
      <div className={inertAll} aria-disabled={preview ? 'true' : undefined} data-testid="event-page-desktop" style={{ background: SOFT.canvas }}>
        <SurfacePage embedded={embedded} bare mobile={framed ? false : null}>
          <main className="mx-auto flex flex-col" style={{ maxWidth: DESKTOP_FRAME, padding: `24px ${DESKTOP_PAD}px 72px`, gap: 20, boxSizing: 'border-box' }}>
            <div className={inertRest}><Breadcrumb backLabel={backLabel} onBack={goBackToEvents} current={crumb(event.title)} /></div>
            <EventHeader event={event} model={model} {...coverProps} />
            <div className={`grid items-start ${inertRest}`} style={{ gridTemplateColumns: wide ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)', gap: 20 }} data-testid="event-body" data-wide={wide ? '1' : '0'}>
              <div className="flex min-w-0 flex-col" style={{ gap: 14 }}>
                {bandNode}
                <SegmentedTabs tabs={tabs} active={tab} onChange={setTab} ariaLabel="Event sections" />
                {tabBody}
              </div>
              {wide ? (
                <StickyRail width={380} top={96}>
                  {railNode('rail')}
                </StickyRail>
              ) : (
                <div className="flex flex-col gap-3" data-testid="static-rail">{railNode('rail')}</div>
              )}
            </div>
          </main>
        </SurfacePage>
        {overlays}
      </div>
    </SoftSurface>
  );
}
